import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { LaborCategory } from '../entities/LaborCategory';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { InFilter } from '../baseServices/filter.interface';
import { Check, EntityHelper } from '../utils/entity-helper';
import { GlobalConstants } from '../constant/constant';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';

@Injectable({
  providedIn: 'root',
})
export class LaborCategoryService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<LaborCategory>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(laborCategory: LaborCategory, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: laborCategory, who: who, what: what });
  }

  getEmitChangeCallback(laborCategory: LaborCategory, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForLaborCategory + '_' + laborCategory.RecordId,
      fn: this.emitChange.bind(this, laborCategory, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.LaborCategory], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getLaborCategoryByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<LaborCategory> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<LaborCategory>(StoreName.LaborCategory, onlineId, LaborCategory, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as LaborCategory;

    if (this.checkIsLaborCategorySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getLaborCategoryByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<LaborCategory> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<LaborCategory>(StoreName.LaborCategory, recordId, LaborCategory, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as LaborCategory;

    if (this.checkIsLaborCategorySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getLaborCategoriesByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<LaborCategory[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<LaborCategory>(StoreName.LaborCategory, GlobalConstants.KEY_NAMES.Id, onlineIds, LaborCategory, transaction);
    let records = result as LaborCategory[];

    if (!includeDeleted) {
      records = result.filter(laborcategory => !this.checkIsLaborCategorySoftDeleted(laborcategory));
    }

    return records;
  }

  async getLaborCategoriesByProjectId(projectId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<LaborCategory[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<LaborCategory>(StoreName.LaborCategory, GlobalConstants.KEY_NAMES.ProjectId, projectId, LaborCategory, transaction);
    let records = result as LaborCategory[];

    if (!includeDeleted) {
      records = result.filter(laborcategory => !this.checkIsLaborCategorySoftDeleted(laborcategory));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createLaborCategory(who: DataChangeEmitWho, recordToCreate: LaborCategory, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateLaborCategoryCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.LaborCategory, transaction);

    // LC5. Get created record
    const createdRecord = await this.getLaborCategoryByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateLaborCategory(
    who: DataChangeEmitWho,
    recordToUpdate: LaborCategory,
    skipLocalCopyValidation: boolean,
    localCopy: LaborCategory | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getLaborCategoryByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateLaborCategoryUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<LaborCategory>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<LaborCategory>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateLaborCategory(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.LaborCategory, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getLaborCategoryByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateLaborCategory(laborCategory: LaborCategory, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateLaborCategoryCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateLaborCategoryUpdate(laborCategory: LaborCategory, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  async getAllActiveValidLaborCategoriesByProjectId(projectId: number, expireComparisonDate: DateTimeType, employeeNumber: string, enableLaborCategoryAssignment: boolean, existingTransaction?: DbTransaction): Promise<LaborCategory[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const laborCategoriesForProject = await this.getLaborCategoriesByProjectId(projectId, transaction);

    const laborCategories = laborCategoriesForProject.filter(laborCategory => 
      this.checkIsLaborCategoryActive(laborCategory, expireComparisonDate) && 
      this.checkLaborCategoryAccessByAssignmentPolicy(laborCategory, employeeNumber, enableLaborCategoryAssignment)
    );

    return laborCategories;
  }

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertLaborCategoryFromSync(who: DataChangeEmitWho, recordToUpsert: LaborCategory, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getLaborCategoryByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateLaborCategory(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createLaborCategory(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsLaborCategorySoftDeleted(record: LaborCategory): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: LaborCategory, b: LaborCategory): boolean {
    //TODO: Update this function
    return a.Number === b.Number && a.OrganizationId === b.OrganizationId;
  }

  checkIsLaborCategoryActive(laborCategory: LaborCategory, expireComparisonDate: DateTimeType): boolean {
    const compareDate = DateTimeHelper.formatDateTimeAsDate(expireComparisonDate);
    return (!laborCategory.StartDate || DateTimeHelper.checkTimeIsAfter(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(laborCategory.StartDate), undefined, true))
      && (!laborCategory.EndDate || DateTimeHelper.checkTimeIsBefore(compareDate, undefined, DateTimeHelper.formatDateTimeAsDate(laborCategory.EndDate), undefined, true));
  }

  checkLaborCategoryAccessByAssignmentPolicy(laborCategory: LaborCategory, employeeNumber: string, assignmentEnabled: boolean): Boolean {
    if (assignmentEnabled) {
      // If assignment is enabled, only show categories assigned to the user
      return laborCategory.EmployeeNumber === employeeNumber;
    } else {
      // If assignment is disabled, only show categories not assigned to anyone
      return laborCategory.EmployeeNumber === null;
    }
  }

  checkIsLaborCategoryAssigned(laborCategory: LaborCategory, employeeNumber: string): Boolean {
    return laborCategory.EmployeeNumber === employeeNumber || laborCategory.EmployeeNumber === null;
  }

  // #endregion
  // region Lookup

  async getLaborCategoryName(laborCategoryId: number, excludeNumber = false, existingTransaction?: DbTransaction): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const laborCategory = await this.getLaborCategoryByOnlineId(laborCategoryId, transaction);

    if (laborCategory === null) {
      throw new RecordNotFoundException(laborCategoryId);
    }

    return excludeNumber ? laborCategory.Name : laborCategory.NameAndNumber;
  }

  async getLaborCategoryNameById(laborCategoryId: number, existingTransaction?: DbTransaction): Promise<string> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    let laborCategory = await this.getLaborCategoryByOnlineId(laborCategoryId, transaction);

    if (laborCategory === null) {
      throw new RecordNotFoundException(laborCategoryId);
    }

    return laborCategory.Name;
  }

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
